import { useEffect, useState } from 'react';
import { ApiError, del, get, post, put, type RundownItemDto } from '../api.js';
import { t } from '../i18n.js';
import { Dialog } from './rundown-dialogs.js';

interface Graphic {
  id: string;
  position: number;
  mosItemId: string;
  mosObjId: string | null;
  templateId: string | null;
  slug: string;
  fields: Record<string, string>;
  channel: string | null;
  readiness: string | null;
}
interface MosObj {
  mosId: string;
  objId: string;
  slug: string;
  type: string;
  status: string;
  air: string;
  description: string | null;
}

/** Graphics of one rundown item: the MOS items of its story. Selecting an existing object is the supported path; a custom form is not proof of template compatibility. */
export function GraphicsDialog({ item, onClose }: { item: RundownItemDto; onClose: () => void }) {
  const [graphics, setGraphics] = useState<Graphic[]>([]);
  const [objects, setObjects] = useState<MosObj[]>([]);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Graphic | null>(null);

  const load = () => get<{ graphics: Graphic[] }>(`/api/items/${item.id}/graphics`).then((r) => setGraphics(r.graphics));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);
  useEffect(() => {
    const h = window.setTimeout(() => get<{ objects: MosObj[] }>(`/api/mos/objects?q=${encodeURIComponent(q)}`).then((r) => setObjects(r.objects)), 200);
    return () => window.clearTimeout(h);
  }, [q]);

  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <Dialog title={`${t('colGraphics')} — [${item.displayNumber ?? ''}] ${item.title}`} onClose={onClose} width={860}>
      <table className="grid">
        <thead>
          <tr>
            <th>#</th>
            <th>Slug</th>
            <th>MOS object</th>
            <th>Template</th>
            <th>Πεδία</th>
            <th>Κανάλι</th>
            <th>Κατάσταση</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {graphics.length === 0 && (
            <tr>
              <td colSpan={8} className="c muted">
                —
              </td>
            </tr>
          )}
          {graphics.map((g, i) => (
            <tr key={g.id}>
              <td className="num">{i + 1}</td>
              <td>{g.slug}</td>
              <td>{g.mosObjId ?? ''}</td>
              <td>{g.templateId ?? ''}</td>
              <td>
                {Object.entries(g.fields).map(([k, v]) => (
                  <div key={k}>
                    <span className="muted">{k}:</span> {v}
                  </div>
                ))}
              </td>
              <td className="c">{g.channel ?? ''}</td>
              <td className="c">{g.readiness ? <span className={`status ${/READY|PLAY/.test(g.readiness) && !/NOT/.test(g.readiness) ? 'ok' : 'warn'}`}>{g.readiness}</span> : <span className="status">{t('pending')}</span>}</td>
              <td className="actions">
                <button title={t('editRecord')} onClick={() => setEditing(g)}>
                  ✎
                </button>
                <button title={t('cut')} onClick={() => void act(() => del(`/api/items/${item.id}/graphics/${g.id}`))}>
                  ✖
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="panel" style={{ padding: 8, marginTop: 8 }}>
          <div className="panel-title">{t('editRecord')}</div>
          <FieldsEditor
            graphic={editing}
            onCancel={() => setEditing(null)}
            onSave={(patch) =>
              void act(async () => {
                await put(`/api/items/${item.id}/graphics/${editing.id}`, patch);
                setEditing(null);
              })
            }
          />
        </div>
      )}

      <div className="panel" style={{ padding: 8, marginTop: 8 }}>
        <div className="panel-title">Προσθήκη από τον κατάλογο Viz / MOS</div>
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
          <label>{t('search')} :</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="slug, objID…" />
        </div>
        <table className="grid">
          <tbody>
            {objects.slice(0, 15).map((o) => (
              <tr key={o.mosId + o.objId}>
                <td>{o.slug}</td>
                <td className="muted">{o.objId}</td>
                <td className="c">{o.type}</td>
                <td className="c">
                  <span className={`status ${o.air === 'READY' ? 'ok' : 'warn'}`}>{o.air}</span>
                </td>
                <td className="c">
                  <button className="btn" onClick={() => void act(() => post(`/api/items/${item.id}/graphics`, { mosObjId: o.objId, slug: o.slug, fields: {}, channel: null }))}>
                    ✚
                  </button>
                </td>
              </tr>
            ))}
            {objects.length === 0 && (
              <tr>
                <td className="muted c">Κανένα αντικείμενο από συσκευή MOS ακόμη (ο κατάλογος γεμίζει με mosObj / mosListAll).</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {error && <div className="banner err">{error}</div>}
      <p className="tip">Η προσθήκη γραφικού στέλνει REPLACE της ιστορίας στο Viz Gateway (μέσω MOS) χωρίς TAKE. Το TAKE/OUT παραμένει στον χειριστή γραφικών.</p>
      <div className="toolbar">
        <button className="btn" onClick={onClose}>
          {t('back')}
        </button>
      </div>
    </Dialog>
  );
}

function FieldsEditor({ graphic, onSave, onCancel }: { graphic: Graphic; onSave: (patch: Partial<Graphic>) => void; onCancel: () => void }) {
  const [slug, setSlug] = useState(graphic.slug);
  const [channel, setChannel] = useState(graphic.channel ?? '');
  const [templateId, setTemplateId] = useState(graphic.templateId ?? '');
  const [fields, setFields] = useState<Array<[string, string]>>(Object.entries(graphic.fields));
  return (
    <div>
      <div className="form-grid">
        <label>Slug :</label>
        <input value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={128} />
        <label>Κανάλι :</label>
        <input value={channel} onChange={(e) => setChannel(e.target.value)} maxLength={40} />
        <label>Template :</label>
        <input value={templateId} onChange={(e) => setTemplateId(e.target.value)} maxLength={200} />
      </div>
      <table className="grid" style={{ width: 'auto' }}>
        <tbody>
          {fields.map(([k, v], i) => (
            <tr key={i}>
              <td>
                <input value={k} onChange={(e) => setFields(fields.map((f, j) => (j === i ? [e.target.value, f[1]] : f)))} placeholder="πεδίο" />
              </td>
              <td>
                <input value={v} onChange={(e) => setFields(fields.map((f, j) => (j === i ? [f[0], e.target.value] : f)))} placeholder="τιμή" style={{ width: 320 }} />
              </td>
              <td>
                <button onClick={() => setFields(fields.filter((_, j) => j !== i))}>✖</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="toolbar left">
        <button className="btn" onClick={() => setFields([...fields, ['', '']])}>
          ✚ πεδίο
        </button>
        <button className="btn" onClick={onCancel}>
          {t('cancel')}
        </button>
        <button className="btn primary" onClick={() => onSave({ slug, channel: channel || null, templateId: templateId || null, fields: Object.fromEntries(fields.filter(([k]) => k.trim())) })}>
          {t('save')}
        </button>
      </div>
    </div>
  );
}
